import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXsrfConfiguration,
} from '@angular/common/http';
//
import localeAf from '@angular/common/locales/af';
import localeCs from '@angular/common/locales/cs';
import localeDe from '@angular/common/locales/de';
import localeEs419 from '@angular/common/locales/es-419';
import localeAfExtras from '@angular/common/locales/extra/af';
// Czech
import localeCsExtra from '@angular/common/locales/extra/cs';
// German
import localeDeExtra from '@angular/common/locales/extra/de';
// Spanish
import localeEs419Extra from '@angular/common/locales/extra/es-419';
// French
import localeFrExtra from '@angular/common/locales/extra/fr';
// French Canadian
import localeFrCaExtra from '@angular/common/locales/extra/fr-CA';
import localeHeExtras from '@angular/common/locales/extra/he';
// Dutch
import localeNlExtra from '@angular/common/locales/extra/nl';
// Portuguese
import localePtExtra from '@angular/common/locales/extra/pt';
// Russian
import localeRuExtras from '@angular/common/locales/extra/ru';
import localeFr from '@angular/common/locales/fr';
import localeFrCa from '@angular/common/locales/fr-CA';
import localeHe from '@angular/common/locales/he';
import localeNl from '@angular/common/locales/nl';
import localePt from '@angular/common/locales/pt';
import localeRu from '@angular/common/locales/ru';
import { APP_INITIALIZER, inject, NgModule } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AtlasConfigService, AtlasDataService, Language } from '@galaxy/atlas/core';
import {
  AutomataI18nModule,
  AUTOMATION_CONTEXT_INJECTION_TOKEN,
  AUTOMATION_DEFAULT_CURRENCY_CODE,
  AUTOMATION_IS_USER_SUPERADMIN$,
  AUTOMATION_MARKET_ID_INJECTION_TOKEN$,
  AUTOMATION_NAMESPACE_INJECTION_TOKEN$,
  AUTOMATION_PARTNER_ID_INJECTION_TOKEN$,
  AUTOMATIONS_ACCESS_SERVICE,
  AUTOMATIONS_MARKETS_SERVICE,
  AUTOMATIONS_ROUTE_PREFIX$,
  BusinessAppAccessService,
  BusinessAppMarketsService,
} from '@galaxy/automata/shared';
import { CampaignConfig, LOCALE_DATA_TYPE, SUPPORTED_LOCALES_TOKEN } from '@galaxy/campaign/dependencies';
import { CONFIG_TOKEN } from '@galaxy/campaign/shared';
import { CoreModule, Environment, EnvironmentService, SessionService } from '@galaxy/core';
import { SENDER_ID_TOKEN } from '@galaxy/email-ui/email-activity/src/dependencies';
import { EmailPreviewHydrationData } from '@galaxy/email-ui/email-builder';
import {
  VariableMenuItem,
  VariableMenuItemOptions,
} from '@galaxy/email-ui/email-builder/src/components/dynamic-component-selector';
import {
  Footer,
  TopLogo,
} from '@galaxy/email-ui/email-builder/src/components/email-advanced-settings/email-advanced-settings.component';
import {
  DYNAMIC_COMPONENT_DATA_TOKEN,
  EMAIL_FOOTER_TOKEN,
  EMAIL_TOP_LOGO_TOKEN,
  TEMPLATE_HYDRATION_DATA_TOKEN,
} from '@galaxy/email-ui/email-builder/src/dependencies/tokens';
import { SENDER_INJECTION_TOKEN } from '@galaxy/email-ui/email-builder/src/shared';
import { SENDER_EMAIL_LIB_TOKEN } from '@galaxy/email-ui/email-library/dependencies';
import { TranslationModule as FormBuilderI18NModule } from '@galaxy/form-builder';
import { LexiconModule } from '@galaxy/lexicon';
import { PartnerServiceInterfaceToken } from '@galaxy/partner';
import {
  PLATFORM_INTEGRATIONS_CONTEXT_INJECTION_TOKEN$,
  PLATFORM_INTEGRATIONS_IS_LOCAL_SEO_INJECTION_TOKEN,
  PLATFORM_INTEGRATIONS_MARKET_ID_INJECTION_TOKEN$,
  PLATFORM_INTEGRATIONS_NAMESPACE_INJECTION_TOKEN$,
  PLATFORM_INTEGRATIONS_ORIGIN_INJECTION_TOKEN,
  PLATFORM_INTEGRATIONS_PARTNER_ID_INJECTION_TOKEN$,
} from '@galaxy/platform-integrations/shared';
import { DEFAULT_LANGUAGE } from '@ngx-translate/core';
import { UITheme } from '@vendasta/atlas';
import { Context } from '@vendasta/automata';
import { BusinessNavConfigService } from '@vendasta/business-nav';
import { Sender, SenderType } from '@vendasta/email';
import { GalaxyI18NModule, getLocale } from '@vendasta/galaxy/i18n';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { GalaxyDefaultProviderOverrides } from '@vendasta/galaxy/provider-default-overrides';
import { GalaxySnackbarModule } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTheme, preferredTheme, StyleService } from '@vendasta/galaxy/style-service';
import { AGIDTOKEN, IS_IMPERSONATING_TOKEN } from '@vendasta/local-seo';
import { SupportedContexts } from '@vendasta/platform-integrations';
import { ProductAnalyticsModule } from '@vendasta/product-analytics';
import { TaxonomyServiceInterfaceToken, TaxonomyServiceModule } from '@vendasta/taxonomy';
import { TaxonomyI18nModule } from '@vendasta/taxonomy-i18n';
import { CookieService } from 'ngx-cookie-service';
import { MarkdownModule } from 'ngx-markdown';
import { combineLatest, distinctUntilChanged, map, Observable, of, shareReplay } from 'rxjs';
import baseTranslation from '../assets/i18n/en_devel.json';
import { partnerId } from '../globals';
import { ACCOUNT_GROUP_ID_TOKEN, ACCOUNT_GROUP_TOKEN } from '../tokens';
import { AccountGroup } from './account-group';
import { AccountGroupService } from './account-group/account-group.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth.module';
import { AuthService } from './auth.service';
import { CampaignConfigFactory } from './campaigns/providers';
import { WEBLATE_COMPONENT_NAME } from './constants';
import { LOCALES } from './core/language-service.service';
import { partnerServiceInitializer } from './core/partner-services/partner-services';
import { PartnerService } from './core/partner.service';
import { TaxonomyService } from './core/taxonomy.service';
import { getHostName, VBC_HOST, VbcHostService } from './core/vbc/vbc.host.service';
import { CrmService } from './crm/crm.service';
import { FailedPaymentsModule } from './failed-payments/failed-payments.module';
import { GlobalNotificationComponent } from './global-notification/global-notification.component';
import { JwtInterceptor } from './interceptors/jwt.interceptor';
import { QueryMetricsThrottleInterceptor } from './interceptors/query-metrics.interceptor';
import { TemporarySessionInterceptor } from './interceptors/temporary-auth.interceptor';
import { LocationsService } from './locations';
import { PartnerMarketConfigService } from './partner-market-config/partner-market-config.service';
import { TimeRangeService } from './shared/time-range.service';
import { BROADLY_SMS_PIDS, SmsContextToken } from '@galaxy/sms';
import { OwnerType } from '@vendasta/smsv2';
import { CurrencyService } from './currency';
import { firebaseConfig } from '@vendasta/shared';
import { AngularFireModule } from '@angular/fire/compat';
import { MEETING_SCHEDULER_CONTEXT_INJECTION_TOKEN$, MS_CONTEXT } from '@galaxy/meeting-scheduler';
import { AppInitializerService } from './app-initialize.service';
import { BusinessCrmModule } from './crm/business-crm.module';
import { MeetingSourceOrigin } from '@vendasta/meetings';
import { REPUTATION_CONFIG } from './reputation/providers';
import { REPUTATION_CONFIG_TOKEN } from '@galaxy/reputation';
import { GALAXY_PAGE_OPTIONS } from '@vendasta/galaxy/page';
import { AddressTemplates } from '@galaxy/email-ui/templates/address-templates';
import {
  MEETING_ANALYSIS_ENABLED_INJECTION_TOKEN$,
  MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$,
  MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$,
  MEETING_ANALYSIS_ROUTE_PREFIX$,
  MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$,
  MeetingAnalysisI18nModule,
} from '@galaxy/meeting-analysis/static';
import { SalesCoachConfigApiService } from '@vendasta/meeting-analysis';
import { catchError, switchMap } from 'rxjs/operators';

export const PARTNER_ID_TOKEN$ = 'partnerId$';
export const MARKET_ID_TOKEN$ = 'marketId$';

registerLocaleData(localeFr, LOCALES.FR, localeFrExtra);
registerLocaleData(localeFrCa, LOCALES.FR_CA, localeFrCaExtra);
registerLocaleData(localeCs, LOCALES.CS, localeCsExtra);
registerLocaleData(localePt, LOCALES.PT, localePtExtra);
registerLocaleData(localeNl, LOCALES.NL, localeNlExtra);
registerLocaleData(localeDe, LOCALES.DE, localeDeExtra);
registerLocaleData(localeEs419, LOCALES.ES_419, localeEs419Extra);
registerLocaleData(localeRu, LOCALES.RU, localeRuExtras);
registerLocaleData(localeAf, LOCALES.AF, localeAfExtras);
registerLocaleData(localeHe, LOCALES.HE, localeHeExtras);

const dynamicComponents = (): VariableMenuItem[] => {
  return [
    {
      textKey: 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.CONTACT_DETAILS',
      replacementTags: ['crmCampaignContactFields'],
      options: {
        nestedDynamicSelector: true,
      } as VariableMenuItemOptions,
    },
    {
      textKey: 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.PRIMARY_COMPANY_DETAILS',
      replacementTags: ['crmCampaignCompanyFields'],
      options: {
        nestedDynamicSelector: true,
      } as VariableMenuItemOptions,
    },
    {
      textKey: 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.ACCOUNT_DETAILS',
      replacementTags: [
        'businessName',
        'businessStreetAddress',
        'businessStreetAddress2',
        'businessCity',
        'businessState',
        'businessCountry',
        'businessPostalCode',
        'businessPhoneNumber',
        'businessWebsite',
      ],
    },
  ];
};

const emailBuilderHydrationData = (): Observable<EmailPreviewHydrationData> => {
  const accountGroup$ = inject(ACCOUNT_GROUP_TOKEN);
  const environmentService = inject(EnvironmentService);
  const contactService = inject(CrmService);
  return combineLatest([accountGroup$, contactService.contactIds$]).pipe(
    map(([accountGroup, contacts]) => {
      return {
        business: {
          accountGroupId: accountGroup.accountGroupId,
          name: accountGroup.companyName,
          hasSnapshot: false,
          hasSalesperson: false,
        },
        userID: getExampleUserId(environmentService.getEnvironment()),
        contactID: contacts?.[0] ?? 'ContactID',
        partnerID: accountGroup.partnerId,
        marketID: 'default',
        locale: 'en',
        useFakeData: true,
      };
    }),
  );
};

const getExampleUserId = (env: Environment): string => {
  let userId = 'U-862a9c0e-e46d-42ca-9f1b-21303a52ca9c';
  if (env === Environment.PROD) {
    userId = 'U-4c8b64a1-4120-40b6-82d2-f2e0daa2fcc1';
  }
  return userId;
};

const emailBuilderFooter = (config: CampaignConfig): Observable<Footer> => {
  return config.location$.pipe(
    map((loc) => {
      const locale = loc ?? '';
      const localeCode = locale.toUpperCase();
      const addressLine: string = AddressTemplates.has(localeCode)
        ? AddressTemplates.get(localeCode)
        : AddressTemplates.get('default');
      return {
        collapsed: true,
        text: `${addressLine}
        <p>
          {{ interestInPartner }} {% if businessWebsite %}<a href="{{ businessWebsite }}" style="color: inherit; text-decoration:underline!important; display:inline-block">{{ businessName }}</a>{% endif %}
          {% if not businessWebsite %}{{ businessName }}{% endif %}.
          {{ preferNoUpdates }}
          <a href="[sendGridUnsubscribeLink]" style="color: inherit; text-decoration:underline!important;">{{ unsubscribeHere }}</a>.
        </p>`,
      };
    }),
  );
};

const emailBuilderTopLogo = (): TopLogo => {
  return {
    collapsed: true,
    defaultSourceUrl: '{{ businessLogo }}',
  };
};

const supportedLocales = (): LOCALE_DATA_TYPE => of([{ value: LOCALES.EN, name: Language.ENGLISH.toString() }]);

export function senderFactory(accountGroupService: AccountGroupService): Observable<Sender> {
  return accountGroupService.currentAccountGroup$.pipe(
    map((accountGroup) => {
      return new Sender({
        type: SenderType.SENDER_TYPE_BUSINESS,
        id: accountGroup.accountGroupId,
      });
    }),
  );
}

function appThemeFactory() {
  const atlasDataService = inject(AtlasDataService);
  const businessNavConfigService = inject(BusinessNavConfigService);
  const atlasConfigService = inject(AtlasConfigService);
  const styleService = inject(StyleService);

  return () => {
    businessNavConfigService.config$.subscribe((config) => {
      atlasConfigService.initialize(
        config.serviceProviderId,
        config.partnerId,
        config.marketId,
        config.accountId,
        config.serviceProviderId,
        config.groupPath,
        '',
      );
    });

    atlasDataService.businessAppTheme$.pipe(distinctUntilChanged()).subscribe((theme) => {
      const userPreferredTheme = localStorage.getItem(preferredTheme) as GalaxyTheme;
      switch (theme) {
        case UITheme.UI_THEME_DARK:
          styleService.update('business-center-client', userPreferredTheme || GalaxyTheme.Dark);
          break;
        case UITheme.UI_THEME_USER_PREFERENCE:
          styleService.useBrowserTheme('business-center-client', true);
          break;
        case UITheme.UI_THEME_LIGHT:
        default:
          styleService.update('business-center-client', userPreferredTheme || GalaxyTheme.Light);
          break;
      }
    });
  };
}

@NgModule({
  declarations: [AppComponent, GlobalNotificationComponent],
  exports: [],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    GoogleMapsModule,
    ProductAnalyticsModule,
    LexiconModule.forRoot({
      componentName: WEBLATE_COMPONENT_NAME,
      baseTranslation: baseTranslation,
    }),
    TaxonomyI18nModule,
    FailedPaymentsModule,
    MarkdownModule.forRoot(),
    GalaxySnackbarModule,
    GalaxyI18NModule,
    AutomataI18nModule,
    FormBuilderI18NModule,
    TaxonomyServiceModule,
    MatSnackBarModule,
    GalaxyPipesModule,
    GalaxyPopoverModule,
    AngularFireModule.initializeApp(firebaseConfig),
    ServiceWorkerModule.register('service-worker.js', {
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AuthModule,
    BusinessCrmModule,
    MeetingAnalysisI18nModule,
  ],
  providers: [
    ...GalaxyDefaultProviderOverrides,
    CookieService,
    PartnerMarketConfigService,
    Title,
    AppInitializerService,
    {
      provide: DEFAULT_LANGUAGE,
      useFactory: () => getLocale(),
    },
    {
      provide: GALAXY_PAGE_OPTIONS,
      useValue: {
        mobileMaxWidth: 0,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: QueryMetricsThrottleInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TemporarySessionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    { provide: PartnerServiceInterfaceToken, useExisting: PartnerService },
    { provide: TaxonomyServiceInterfaceToken, useExisting: TaxonomyService },
    { provide: VBC_HOST, deps: [VbcHostService], useFactory: getHostName },
    // Several partner ID tokens are necessary because both 'partnerId$' and 'PARTNER_ID'
    // are used in several other apps and libs.
    {
      provide: PARTNER_ID_TOKEN$,
      useFactory: (): Observable<string> => {
        return of(partnerId);
      },
    },
    {
      provide: 'PARTNER_ID',
      useExisting: PARTNER_ID_TOKEN$,
    },
    {
      provide: MARKET_ID_TOKEN$,
      useFactory: (): Observable<string> => {
        return inject(LocationsService).marketId$;
      },
    },
    {
      provide: 'MARKET_ID',
      useExisting: MARKET_ID_TOKEN$,
    },
    {
      provide: SENDER_ID_TOKEN,
      useFactory: (): Observable<string> => {
        return inject(AccountGroupService).currentAccountGroup$.pipe(
          map((accountGroup) => accountGroup.accountGroupId),
        );
      },
    },
    {
      provide: SENDER_INJECTION_TOKEN,
      useFactory: senderFactory,
      deps: [AccountGroupService],
    },
    {
      provide: CONFIG_TOKEN,
      useFactory: CampaignConfigFactory,
    },
    {
      provide: MEETING_SCHEDULER_CONTEXT_INJECTION_TOKEN$,
      useValue: MS_CONTEXT.MS_CONTEXT_SMB,
    },
    {
      provide: SENDER_EMAIL_LIB_TOKEN,
      useFactory: senderFactory,
      deps: [AccountGroupService],
    },
    {
      provide: ACCOUNT_GROUP_TOKEN,
      useFactory: (): Observable<AccountGroup> => inject(AccountGroupService).currentAccountGroup$,
    },
    {
      provide: ACCOUNT_GROUP_ID_TOKEN,
      useFactory: (): Observable<string> => inject(AccountGroupService).currentAccountGroupId$,
    },
    {
      provide: EMAIL_FOOTER_TOKEN,
      useFactory: emailBuilderFooter,
      deps: [CONFIG_TOKEN],
    },
    {
      provide: EMAIL_TOP_LOGO_TOKEN,
      useFactory: emailBuilderTopLogo,
    },
    {
      provide: DYNAMIC_COMPONENT_DATA_TOKEN,
      useFactory: dynamicComponents,
    },
    {
      provide: TEMPLATE_HYDRATION_DATA_TOKEN,
      useFactory: emailBuilderHydrationData,
    },
    {
      provide: SUPPORTED_LOCALES_TOKEN,
      useFactory: supportedLocales,
    },
    {
      provide: 'USER_ID',
      useFactory: (): Observable<string> => {
        return toObservable(inject(AuthService).userId);
      },
    },
    {
      provide: 'IS_IMPERSONATING',
      useFactory: (): Observable<boolean> => {
        return toObservable(inject(AuthService).isImpersonating);
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const sessionService = inject(SessionService);
        const authService = inject(AuthService);
        return () => {
          const sessionIdParam = new URLSearchParams(window.location.search).get('session_id');
          if (sessionIdParam) {
            sessionService.setSessionId(sessionIdParam);
            authService.createVBCSession(sessionIdParam, partnerId).subscribe();
          }
        };
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (appInitializerService: AppInitializerService) => appInitializerService.initializeApp(),
      deps: [AppInitializerService],
      multi: true,
    },
    // Hack to make sure TimeRangeService constructed before the first navigation ends
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        return () => {
          /* */
        };
      },
      deps: [TimeRangeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appThemeFactory,
      deps: [AtlasDataService, BusinessNavConfigService, AtlasConfigService, StyleService],
      multi: true,
    },
    {
      provide: AGIDTOKEN,
      useFactory: (locationService: LocationsService) =>
        locationService.currentAccountGroupIds$.pipe(
          map((agids) => {
            return agids.length > 0 ? agids[0] : '';
          }),
        ),
      deps: [LocationsService],
    },
    {
      provide: IS_IMPERSONATING_TOKEN,
      useFactory: (authService: AuthService) => toObservable(authService.isImpersonating),
      deps: [AuthService],
    },
    partnerServiceInitializer,
    {
      provide: AUTOMATION_NAMESPACE_INJECTION_TOKEN$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.accountGroupId)),
      deps: [AccountGroupService],
    },
    {
      provide: PLATFORM_INTEGRATIONS_NAMESPACE_INJECTION_TOKEN$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.accountGroupId)),
      deps: [AccountGroupService],
    },
    {
      provide: PLATFORM_INTEGRATIONS_ORIGIN_INJECTION_TOKEN,
      useValue: MeetingSourceOrigin.BusinessApp,
    },
    {
      provide: PLATFORM_INTEGRATIONS_CONTEXT_INJECTION_TOKEN$,
      useValue: SupportedContexts.PI_CONTEXT_SMB,
    },
    {
      provide: PLATFORM_INTEGRATIONS_IS_LOCAL_SEO_INJECTION_TOKEN,
      useValue: false,
    },
    {
      provide: PLATFORM_INTEGRATIONS_PARTNER_ID_INJECTION_TOKEN$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.partnerId)),
      deps: [AccountGroupService],
    },
    {
      provide: AUTOMATION_PARTNER_ID_INJECTION_TOKEN$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.partnerId)),
      deps: [AccountGroupService],
    },
    {
      provide: AUTOMATION_MARKET_ID_INJECTION_TOKEN$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.marketId)),
      deps: [AccountGroupService],
    },
    {
      provide: PLATFORM_INTEGRATIONS_MARKET_ID_INJECTION_TOKEN$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.marketId)),
      deps: [AccountGroupService],
    },
    {
      provide: AUTOMATION_CONTEXT_INJECTION_TOKEN,
      useValue: Context.AUTOMATION_CONTEXT_SMB,
    },
    {
      provide: AUTOMATION_IS_USER_SUPERADMIN$,
      useFactory: (): Observable<boolean> => {
        return inject(AuthService).currentUser$.pipe(
          map((user) => {
            return user?.partnerId === 'VA' && partnerId !== 'VA';
          }),
          shareReplay({ refCount: false, bufferSize: 1 }),
        );
      },
    },
    {
      provide: AUTOMATION_DEFAULT_CURRENCY_CODE,
      useFactory: (currencyService: CurrencyService) => {
        return currencyService.getMarketCurrency();
      },
      deps: [CurrencyService],
    },
    {
      provide: AUTOMATIONS_ACCESS_SERVICE,
      useClass: BusinessAppAccessService,
    },
    {
      provide: AUTOMATIONS_MARKETS_SERVICE,
      useClass: BusinessAppMarketsService,
    },
    {
      provide: AUTOMATIONS_ROUTE_PREFIX$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(
          map((accountGroup) => `/account/location/${accountGroup.accountGroupId}`),
        ),
      deps: [AccountGroupService],
    },
    {
      provide: MEETING_ANALYSIS_ROUTE_PREFIX$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(
          map((accountGroup) => `/account/location/${accountGroup.accountGroupId}`),
        ),
      deps: [AccountGroupService],
    },
    {
      provide: MEETING_ANALYSIS_ENABLED_INJECTION_TOKEN$,
      useFactory: (
        salesCoachConfigApiService: SalesCoachConfigApiService,
        accountGroupService: AccountGroupService,
      ) => {
        return accountGroupService.currentAccountGroup$.pipe(
          switchMap((accountGroup) => {
            return salesCoachConfigApiService.get({ namespace: accountGroup.accountGroupId }).pipe(
              map((resp) => resp?.salesCoachConfig?.joinMeetings),
              catchError(() => of(false)), // Fallback to false if the API call fails (not found is expected)
            )
          }),
          catchError(() => of(false)),
          distinctUntilChanged(),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
      },
      deps: [SalesCoachConfigApiService, AccountGroupService],
    },
    {
      provide: MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.accountGroupId)),
      deps: [AccountGroupService],
    },
    {
      provide: MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$,
      useFactory: (accountGroupService: AccountGroupService) =>
        accountGroupService.currentAccountGroup$.pipe(
          map((accountGroup) => accountGroup.companyName),
          shareReplay({ bufferSize: 1, refCount: true }),
        ),
      deps: [AccountGroupService],
    },
    {
      provide: MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$,
      useFactory: (userId$: Observable<string>) => userId$,
      deps: ['USER_ID'],
    },
    {
      provide: SmsContextToken,
      useFactory: (accountGroupService: AccountGroupService) => ({
        ownerId$: accountGroupService.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.accountGroupId)),
        ownerType: OwnerType.OWNER_TYPE_ACCOUNT_GROUP,
        featureFlagInfo$: accountGroupService.currentAccountGroup$.pipe(
          map((accountGroup) => {
            return {
              partnerId: accountGroup.partnerId,
              marketId: accountGroup.marketId,
            };
          }),
        ),
        sendsViaBroadly$: accountGroupService.currentAccountGroup$.pipe(
          map((accountGroup) => {
            return BROADLY_SMS_PIDS.includes(accountGroup.partnerId) && !!accountGroup.externalCustomerId;
          }),
        ),
      }),
      deps: [AccountGroupService],
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({
        headerName: 'x-blast-shield',
        cookieName: 'x-blast-shield',
      }),
    ),
    {
      provide: 'show_my_meetings_recording_tab$',
      useFactory: (salesCoachEnabled$: Observable<boolean>) => salesCoachEnabled$,
      deps: [MEETING_ANALYSIS_ENABLED_INJECTION_TOKEN$],
    },
    {
      provide: REPUTATION_CONFIG_TOKEN,
      useExisting: REPUTATION_CONFIG,
    },
  ],
})
export class AppModule {}
